import React from 'react';
import {
  MsalProvider,
  MsalAuthenticationTemplate,
  MsalAuthenticationResult,
} from '@azure/msal-react';
import {
  InteractionType,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { msalInstance } from './msalInstance';
import { loginRequest, isAuthBypassed } from './config';
import { SimpleAuthProvider } from './SimpleAuth';

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Loading component shown during authentication
 */
const AuthLoading: React.FC = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
    }}
  >
    <div>Authenticating...</div>
  </div>
);

/**
 * Error component shown when authentication fails
 */
const AuthError: React.FC<MsalAuthenticationResult> = ({ error }) => {
  const isInteractionRequired = error instanceof InteractionRequiredAuthError;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        padding: '20px',
        textAlign: 'center',
      }}
    >
      <h2>Authentication Error</h2>
      <p>
        {isInteractionRequired
          ? 'Please sign in to continue.'
          : 'An error occurred during authentication.'}
      </p>
      <p style={{ color: '#666', fontSize: '14px' }}>
        {error?.message || 'Unknown error'}
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{
          marginTop: '20px',
          padding: '10px 20px',
          backgroundColor: '#0078d4',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
        }}
      >
        Retry
      </button>
    </div>
  );
};

/**
 * Auth provider that wraps the application with MSAL authentication or simple auth
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // If auth is bypassed, use simple username/password authentication
  if (isAuthBypassed) {
    return <SimpleAuthProvider>{children}</SimpleAuthProvider>;
  }

  return (
    <MsalProvider instance={msalInstance}>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={loginRequest}
        errorComponent={AuthError}
        loadingComponent={AuthLoading}
      >
        {children}
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
};
